import { useCallback, useEffect, useState, type FormEvent } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ANALYTICS_EVENTS, isPhone } from '@neke/core';
import type { PublicConfig } from '../api/client';
import { ApiError, api } from '../api/client';
import { useSession } from '../store/session';
import { routeOf, track } from '../analytics';

const API = import.meta.env.VITE_API_URL ?? 'http://localhost:3000';

/**
 * Состояние загрузки конфигурации.
 *
 * Три значения, а не «конфиг или null»: «сервер сказал, что Google выключен» и
 * «до сервера не достучались» — разные беды с разным лечением. В первом случае
 * повторять нечего, во втором повтор и есть всё лечение. Пока их не различали,
 * страница входа при недоступном API показывала один заголовок и ничего
 * больше — человек не мог понять, сломалось что-то или так задумано.
 */
type ConfigState =
  | { kind: 'loading' }
  | { kind: 'ready'; config: PublicConfig }
  | { kind: 'failed' };

/** Вход в существующую учётку или заведение новой. */
type Mode = 'login' | 'register';

/**
 * Вход и регистрация по номеру телефона.
 *
 * Форма показывается всегда и не ждёт `/api/config`: этот запрос нужен ровно
 * затем, чтобы решить, рисовать ли кнопку Google. Держать из-за него телефонный
 * вход на экране загрузки значило бы гасить рабочий способ войти из-за
 * недоступности справки о другом.
 */
export function Login() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const setUser = useSession((s) => s.setUser);

  const [state, setState] = useState<ConfigState>({ kind: 'loading' });
  const [mode, setMode] = useState<Mode>('login');
  const [phone, setPhone] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const next = params.get('next') ?? '/result';

  const load = useCallback(() => {
    setState({ kind: 'loading' });
    void api
      .config()
      .then((config) => {
        /**
         * Пустой ответ — тоже отказ, и проверка здесь не перестраховка.
         * `request` разбирает тело как `res.json().catch(() => null)`, поэтому
         * успешный ответ не-JSON-ом возвращается как `null`, а не бросает
         * ошибку. Ровно так выглядит боевая авария, когда `/api` уходит не в
         * API, а во фронт: nginx отдаёт index.html с кодом 200. Без этой
         * строки экран снова замолчал бы.
         */
        setState(config ? { kind: 'ready', config } : { kind: 'failed' });
      })
      .catch(() => setState({ kind: 'failed' }));
  }, []);

  useEffect(load, [load]);

  /** Переключение вкладки чистит отказ: он относился к прошлой форме. */
  const switchTo = (to: Mode) => {
    setMode(to);
    setError(null);
  };

  // Кнопку гасим только по тому, что видно в самой форме. Правильность номера
  // и годность пароля всё равно решает сервер — здесь это лишь способ не
  // отправлять заведомо пустое.
  const phoneLooksRight = isPhone(phone);
  const canSubmit =
    !busy && phoneLooksRight && password.length > 0 && (mode === 'login' || name.trim().length > 1);

  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (!canSubmit) return;

    setBusy(true);
    setError(null);
    void track(ANALYTICS_EVENTS.loginStarted, { method: 'phone', next_route: routeOf(next) });

    const request =
      mode === 'register'
        ? api.register({ phone, password, name: name.trim() })
        : api.loginByPhone({ phone, password });

    void request
      .then((data) => {
        setUser(data.user);
        // Пол и согласие в форме входа не спрашиваются — без них дальше идти
        // некуда, и человек отправляется туда же, куда после Google.
        const needsOnboarding = !data.user.sex || !data.user.hasConsent;
        const target = needsOnboarding ? `/onboarding?next=${encodeURIComponent(next)}` : next;
        const props = { needs_onboarding: needsOnboarding, next_route: routeOf(next) };

        // Регистрация отмечается отдельным событием: в воронке «завёл учётку»
        // и «вернулся» — разные ступени.
        void track(
          mode === 'register' ? ANALYTICS_EVENTS.signedUp : ANALYTICS_EVENTS.signedIn,
          mode === 'register'
            ? { method: 'phone', ...props }
            : { method: 'phone', success: true, ...props },
        );
        navigate(target, { replace: true });
      })
      .catch((e: unknown) => {
        setError(e instanceof ApiError ? e.message : t('common.error'));
        if (mode === 'login') {
          void track(ANALYTICS_EVENTS.signedIn, {
            method: 'phone',
            success: false,
            needs_onboarding: false,
            next_route: routeOf(next),
          });
        }
      })
      .finally(() => setBusy(false));
  };

  return (
    <div className="page">
      <h1>{mode === 'login' ? t('auth.title') : t('auth.registerTitle')}</h1>

      <div className="card stack">
        <div className="btn-row">
          <button
            type="button"
            className={mode === 'login' ? 'btn btn-primary' : 'btn btn-ghost'}
            onClick={() => switchTo('login')}
          >
            {t('auth.tabLogin')}
          </button>
          <button
            type="button"
            className={mode === 'register' ? 'btn btn-primary' : 'btn btn-ghost'}
            onClick={() => switchTo('register')}
          >
            {t('auth.tabRegister')}
          </button>
        </div>

        <form className="stack" onSubmit={submit}>
          {mode === 'register' && (
            <label className="field">
              <span>{t('auth.name')}</span>
              <input
                type="text"
                autoComplete="name"
                value={name}
                onChange={(e) => setName(e.target.value)}
              />
            </label>
          )}

          <label className="field">
            <span>{t('auth.phone')}</span>
            <input
              type="tel"
              // Клавиатура телефона на мобильных и автоподстановка номера из
              // менеджера паролей: набирать двенадцать символов руками —
              // первое, на чём люди бросают регистрацию.
              inputMode="tel"
              autoComplete="tel"
              placeholder={t('auth.phonePlaceholder')}
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
            />
            {/* Подсказка про формат — только когда человек уже что-то набрал:
                показывать её пустому полю значит ругаться до первой попытки. */}
            {phone.length > 0 && !phoneLooksRight && (
              <span className="muted">{t('auth.phoneHint')}</span>
            )}
          </label>

          <label className="field">
            <span>{t('auth.password')}</span>
            <input
              type="password"
              // Менеджеру паролей нужно знать, новый это пароль или прежний:
              // иначе на регистрации он подставит старый, а на входе предложит
              // сохранить ещё один.
              autoComplete={mode === 'register' ? 'new-password' : 'current-password'}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
            />
            {mode === 'register' && <span className="muted">{t('auth.passwordHint')}</span>}
          </label>

          {error && <p className="error">{error}</p>}

          <button className="btn btn-primary" type="submit" disabled={!canSubmit}>
            {busy
              ? t('common.loading')
              : mode === 'login'
                ? t('auth.submitLogin')
                : t('auth.submitRegister')}
          </button>
        </form>
      </div>

      {state.kind === 'loading' && <p className="muted">{t('common.loading')}</p>}

      {/* Конфигурация не доехала — про Google сказать нечего, но телефонный
          вход выше при этом работает, поэтому это заметка, а не пустой экран. */}
      {state.kind === 'failed' && (
        <div className="stack">
          <p className="notice">{t('auth.configFailed')}</p>
          <button className="btn btn-ghost" onClick={load}>
            {t('common.retry')}
          </button>
        </div>
      )}

      {state.kind === 'ready' && state.config.googleEnabled && (
        <a
          className="btn btn-ghost"
          href={`${API}/api/auth/google?next=${encodeURIComponent(next)}`}
          // Уход на Google — конец сессии страницы, поэтому событие шлём
          // здесь: вернётся человек уже на /auth/callback.
          onClick={() =>
            void track(ANALYTICS_EVENTS.loginStarted, {
              method: 'google',
              next_route: routeOf(next),
            })
          }
        >
          {t('auth.google')}
        </a>
      )}
    </div>
  );
}
